import { onScopeDispose } from "vue";

// Reference count shared across every consumer in the page. This is required
// so that nested SpDialog / SpBottomSheet instances cooperate: when an inner
// dialog closes it must NOT release the page lock while an outer dialog is
// still open. The lock is only actually released when the count drops to 0.
let lockCount = 0;
let previousOverflow = null;
let previousPaddingRight = null;

function applyLock() {
  if (typeof document === "undefined") {
    return;
  }
  const html = document.documentElement;
  // overflow:hidden removes the viewport scrollbar; reserve its width as
  // padding so the page content does not shift sideways when the lock engages.
  const scrollbarWidth = window.innerWidth - html.clientWidth;
  previousOverflow = html.style.overflow;
  previousPaddingRight = html.style.paddingRight;
  html.style.overflow = "hidden";
  if (scrollbarWidth > 0) {
    const basePaddingRight = Number.parseFloat(getComputedStyle(html).paddingRight) || 0;
    html.style.paddingRight = `${basePaddingRight + scrollbarWidth}px`;
  }
}

function releaseLock() {
  if (typeof document === "undefined") {
    return;
  }
  const html = document.documentElement;
  restoreStyle(html, "overflow", previousOverflow);
  restoreStyle(html, "padding-right", previousPaddingRight);
  previousOverflow = null;
  previousPaddingRight = null;
}

// Restore an inline style to its prior value, or remove it entirely when there
// was none. Setting the property to "" does not reliably remove it, so an empty
// prior value uses removeProperty.
function restoreStyle(html, property, value) {
  if (value) {
    html.style.setProperty(property, value);
  } else {
    html.style.removeProperty(property);
  }
}

/**
 * Reference-counted body scroll lock that targets `<html>` (the element that
 * actually scrolls the viewport).
 *
 * Each consumer gets a `lock()`/`unlock()` pair that is idempotent for that
 * consumer: calling `lock()` twice without an intervening `unlock()` only
 * counts once. The shared lock is released when the last consumer unlocks.
 *
 * Auto-releases on scope dispose so a component being torn down while open
 * doesn't strand the page in a locked state.
 *
 * @returns {{ lock: () => void, unlock: () => void }}
 */
export function useBodyScrollLock() {
  let owns = false;

  function lock() {
    if (owns) {
      return;
    }
    owns = true;
    lockCount += 1;
    if (lockCount === 1) {
      applyLock();
    }
  }

  function unlock() {
    if (!owns) {
      return;
    }
    owns = false;
    lockCount -= 1;
    if (lockCount === 0) {
      releaseLock();
    }
  }

  onScopeDispose(unlock);

  return { lock, unlock };
}
